import { EVIDENCE_KINDS_V2 } from "./primitives.js";

const DECIMAL_SCALE_DIGITS = 8;
const DECIMAL_SCALE = 100_000_000n;
const DECIMAL_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d{1,8})?$/u;

export function exact<const T extends readonly string[]>(value: unknown, keys: T): value is Record<T[number], any> {
  return record(value) && Object.keys(value).length === keys.length && allowedKeys(value, keys);
}

export function allowedKeys(value: Record<string, unknown>, keys: readonly string[]) {
  return Object.keys(value).every((key) => keys.includes(key));
}

export function record(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function text(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function nullableText(value: unknown): value is string | null { return value === null || text(value); }
export function currency(value: unknown): value is string { return typeof value === "string" && /^[A-Z]{3,6}$/u.test(value); }
export function integer(value: unknown, minimum: number) { return Number.isSafeInteger(value) && Number(value) >= minimum; }
export function member<T>(value: unknown, allowed: readonly T[]): value is T { return allowed.includes(value as T); }
export function fingerprint(value: unknown) { return typeof value === "string" && /^sha256:v1:[0-9a-f]{64}$/u.test(value); }
export function nonnegativeDecimal(value: unknown): value is string { return typeof value === "string" && /^(?:0|[1-9]\d*)(?:\.\d+)?$/u.test(value); }
export function positiveDecimal(value: unknown): value is string { return nonnegativeDecimal(value) && /[1-9]/u.test(value); }
export function canonicalDecimal(value: unknown): value is string {
  if (typeof value !== "string" || !DECIMAL_PATTERN.test(value)) return false;
  return decimalFromScaled(decimalToScaled(value)) === value;
}
export function compareDecimal(left: string, right: string): -1 | 0 | 1 {
  const difference = decimalToScaled(left) - decimalToScaled(right);
  return difference < 0n ? -1 : difference > 0n ? 1 : 0;
}
export function addDecimals(...values: string[]): string {
  return decimalFromScaled(values.reduce((total, value) => total + decimalToScaled(value), 0n));
}
export function subtractDecimals(left: string, right: string): string {
  return decimalFromScaled(decimalToScaled(left) - decimalToScaled(right));
}
export function multiplyDecimals(left: string, right: string): string {
  return decimalFromScaled(divideRounded(decimalToScaled(left) * decimalToScaled(right), DECIMAL_SCALE));
}
export function isoOrNull(value: unknown): value is string | null { return value === null || iso(value); }
export function iso(value: unknown): value is string { return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(value) && new Date(value).toISOString() === value; }
export function issueValue(value: unknown) { return value === null || typeof value === "string" || typeof value === "boolean"; }

export function evidenceReference(value: unknown): boolean {
  return record(value) && allowedKeys(value, ["evidenceId", "kind", "knownAt", "sourceId"])
    && text(value.evidenceId) && member(value.kind, EVIDENCE_KINDS_V2) && iso(value.knownAt)
    && (value.sourceId === undefined || text(value.sourceId));
}

export function revocationEvidence(value: unknown): boolean {
  return exact(value, ["revokedAt", "ownerDecisionId", "reason"])
    && iso(value.revokedAt) && text(value.ownerDecisionId) && text(value.reason);
}

export function sameJson(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (Array.isArray(left) || Array.isArray(right)) return Array.isArray(left) && Array.isArray(right)
    && left.length === right.length && left.every((item, index) => sameJson(item, right[index]));
  if (!record(left) || !record(right)) return false;
  const keys = Object.keys(left).sort();
  return keys.length === Object.keys(right).length && keys.every((key) => Object.hasOwn(right, key) && sameJson(left[key], right[key]));
}

function decimalToScaled(value: string): bigint {
  if (!DECIMAL_PATTERN.test(value)) throw new Error("INVALID_DECIMAL");
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ""] = unsigned.split(".");
  const scaled = BigInt(whole) * DECIMAL_SCALE + BigInt(fraction.padEnd(DECIMAL_SCALE_DIGITS, "0"));
  return negative ? -scaled : scaled;
}

function decimalFromScaled(value: bigint): string {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const whole = absolute / DECIMAL_SCALE;
  const fraction = (absolute % DECIMAL_SCALE).toString().padStart(DECIMAL_SCALE_DIGITS, "0").replace(/0+$/u, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

function divideRounded(numerator: bigint, denominator: bigint): bigint {
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = numerator < 0n ? -numerator : numerator;
  const absoluteDenominator = denominator < 0n ? -denominator : denominator;
  const quotient = absoluteNumerator / absoluteDenominator;
  const remainder = absoluteNumerator % absoluteDenominator;
  const rounded = remainder * 2n >= absoluteDenominator ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}
